import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
import { locationResponse } from "~/backend/validate.js";
/** @import { adminMachineResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest, paginatedResponse, query } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
/** @import { Placement } from "~/util/float.js"; */
import { popover } from "~/util/popover.js";
import Query from "~/util/query.js";
import Icon from "~/component/Icon.js";
import Combobox from "~/admin/component/Combobox.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./setLocation.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./setLocation.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @param {{ anchor: HTMLButtonElement; orgId: string; placement: Placement }} props
 * @param {adminMachineResponse[]} machines
 */
export const setLocation = async ({
  anchor,
  orgId,
  placement
}, ...machines) => {
  const machine_ids = machines.map(machine => machine.id);
  const removable = machines.some(machine => machine.location);
  const res = await popover({
    anchor,
    placement,
    content: (/** @type {(result?: string | null) => void} */resolve) => h(PopoverChildren, {
      resolve,
      orgId,
      removable
    })
  });
  if (res === undefined) return;
  try {
    await authedApiRequest("PUT", `/v2/orgs/${orgId}/machines/location`, {
      json: {
        machine_ids,
        location_id: res ?? ""
      }
    });
    Query.invalidate([`/ui/admin/orgs/${orgId}/machines`], ...machine_ids.map(id => [`/ui/admin/orgs/${orgId}/machines/${id}`]), [`/v2/orgs/${orgId}/locations`]);
    return true;
  } catch (err) {
    alert(ApiError.from(err).message, {
      type: "error"
    });
    return;
  }
};

/**
 * @extends {Component<PopoverChildrenProps>}
 *
 * @typedef {object} PopoverChildrenProps
 * @property {string} orgId
 * @property {(result?: string | null) => void} resolve
 * @property {boolean} [removable]
 */
class PopoverChildren extends Component {
  query = signal("");
  queryLocations = query(() => ({
    key: [`/v2/orgs/${this.props.orgId}/locations`, {
      limit: 10,
      q: this.query.value
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(locationResponse)
  });
  render() {
    const options = this.queryLocations.data?.data.map(location => ({
      id: location.id,
      label: location.name,
      render: () => __h("div", {
        "class": css.location
      }, __h(Icon, {
        name: "antenna",
        size: 14
      }), __h("div", {
        "class": css.text + " " + util.truncate
      }, location.name))
    })) ?? [];
    return __h("div", {
      "class": css.popover
    }, __h("div", {
      "class": css.switcher
    }, __h("button", {
      type: "button",
      style: "--active-text: var(--color-link);",
      "data-active": true
    }, "Assign"), __h("button", {
      type: "button",
      style: "--active-text: var(--color-error-foreground);",
      disabled: !this.props.removable,
      onClick: () => this.props.resolve(null)
    }, "Remove")), h(Combobox, {
      onSelect: option => this.props.resolve(option.id),
      query: this.query,
      debounce: 200,
      loading: this.queryLocations.loading,
      options
    }));
  }
}